import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { arrayUtils, Checkbox, Label } from '@sato/components';
import * as yup from 'yup';

import { IWidget, ParsedEntry } from '@/contentful/types';

import { api } from '@api';
import { HookFormCheckbox } from '@components/ReactHookFormFields/HookFormCheckbox';
import { HookFormTextArea } from '@components/ReactHookFormFields/HookFormTextArea';
import { useIncidentCreation } from '@hooks/incidents';
import { useCustomTranslations } from '@hooks/useCustomTranslations';
import {
  IncidentClassification,
  IncidentSubClassification,
} from '@models/Incident';

import { IncidentFormWrapper } from '../IncidentFormWrapper';

interface RenovationForm {
  painting: string;
  wallpapering: string;
  fixtures: string;
  other: string;
  schedule: string;
  terms: boolean;
}

export const RenovationDIYForm = ({
  customTranslations,
}: ParsedEntry<IWidget>) => {
  const { t } = useCustomTranslations(customTranslations);

  const { create } = useIncidentCreation({
    postIncident: api.incidents.postApartmentMaintenanceIncident,
    classifications: {
      classification: IncidentClassification.APARTMENT_CONDITION,
      subClassification: IncidentSubClassification.RENOVATE,
    },
  });

  return (
    <IncidentFormWrapper<RenovationForm>
      incidentKind="RenovationDIY"
      defaultValues={{
        fixtures: '',
        other: '',
        painting: '',
        schedule: '',
        wallpapering: '',
        terms: false,
      }}
      validationSchema={yup.object().shape({
        fixtures: yup.string().required(),
        other: yup.string().required(),
        painting: yup.string().required(),
        schedule: yup.string().required(),
        wallpapering: yup.string().required(),
        terms: yup.bool().isTrue(t('termsValidationError')).required(),
      })}
      onSubmit={(values) =>
        create({
          ...values,
          title: t('messageTitle'),
          description: generateRenovateDescription(values, t),
        })
      }
    >
      <HookFormFieldWithCheckboxToggle
        name="painting"
        label={t('paintingLabel')}
        checkboxLabel={t('paintingCheckboxLabel')}
        inputLabel={t('paintingInputLabel')}
        inputHelper={t('paintingInputHelper')}
        placeholder={t('paintingInputPlaceholder')}
      />

      <HookFormFieldWithCheckboxToggle
        name="wallpapering"
        label={t('wallpaperLabel')}
        checkboxLabel={t('wallpaperCheckboxLabel')}
        inputLabel={t('wallpaperInputLabel')}
        inputHelper={t('wallpaperInputHelper')}
        placeholder={t('wallpaperInputPlaceholder')}
      />

      <HookFormFieldWithCheckboxToggle
        name="fixtures"
        label={t('fixturesLabel')}
        checkboxLabel={t('fixturesCheckboxLabel')}
        inputLabel={t('fixturesInputLabel')}
        inputHelper={t('fixturesInputHelper')}
        placeholder={t('fixturesInputPlaceholder')}
      />

      <HookFormFieldWithCheckboxToggle
        name="other"
        label={t('otherLabel')}
        checkboxLabel={t('otherCheckboxLabel')}
        inputLabel={t('otherInputLabel')}
        inputHelper={t('otherInputHelper')}
        placeholder={t('otherInputPlaceholder')}
      />

      <HookFormFieldWithCheckboxToggle
        name="schedule"
        label={t('scheduleLabel')}
        checkboxLabel={t('scheduleCheckboxLabel')}
        inputLabel={t('scheduleInputLabel')}
        inputHelper={t('scheduleInputHelper')}
        placeholder={t('scheduleInputPlaceholder')}
        hideCheckbox
      />

      <HookFormCheckbox name="terms" label={t('termsLabel')} />
    </IncidentFormWrapper>
  );
};

/** A field with accompanied checkbox that can be used to disable the text input */
const HookFormFieldWithCheckboxToggle = ({
  name,
  label,
  checkboxLabel,
  inputLabel,
  inputHelper,
  placeholder,
  hideCheckbox = false,
}: {
  name: string;
  label: string;
  checkboxLabel: string;
  inputLabel: string;
  inputHelper?: string;
  placeholder?: string;
  hideCheckbox?: boolean;
}) => {
  const [disabled, setDisabled] = useState(false);
  const { setValue } = useFormContext();

  return (
    <>
      <Label>{label}</Label>

      {!hideCheckbox && (
        <Checkbox
          isSelected={disabled}
          label={checkboxLabel}
          onChange={(checked) => {
            // Set the checkbox label as field value
            // Eg. checking "I don't want to paint" sets that as `painting` value
            setValue(name, checked ? checkboxLabel : undefined);

            setDisabled(checked);
          }}
        />
      )}

      <HookFormTextArea
        maxLength={360}
        isDisabled={disabled}
        name={name}
        rows={6}
        message={inputHelper}
        label={{ type: 'static', value: inputLabel }}
        placeholder={placeholder}
      />
    </>
  );
};

const generateRenovateDescription = (
  { painting, wallpapering, fixtures, other, schedule }: RenovationForm,
  translationFn: (t: string) => string
) => {
  const createRow = (labelKey: string, value?: string) =>
    value
      ? [`${translationFn(labelKey)}:`, `- ${value}`].join('\n')
      : undefined;

  return arrayUtils.joinArrayString(
    [
      createRow('paintingLabel', painting),
      createRow('wallpaperLabel', wallpapering),
      createRow('fixturesLabel', fixtures),
      createRow('otherLabel', other),
      createRow('scheduleLabel', schedule),
    ],
    '\n\n'
  );
};
